import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { articleContent } from "@/mocks/article";
import { siteConfig, categories } from "@/mocks/categories";
import JsonLd from "@/components/base/JsonLd";

const articleJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: articleContent.title,
    description: articleContent.summary,
    author: {
      "@type": "Organization",
      name: "GTBuy Spreadsheet",
      url: siteConfig.siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "GTBuy Spreadsheet",
      url: siteConfig.siteUrl,
    },
    datePublished: "2026-07-01",
    dateModified: "2026-07-01",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteConfig.siteUrl}/article/gtbuy-spreadsheet-guide`,
    },
    about: {
      "@type": "Thing",
      name: "GTBuy Spreadsheet Product Comparison Guide",
      description:
        "A comprehensive guide to using the GTBuy spreadsheet system for smarter shopping decisions across all product categories.",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: siteConfig.siteUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "GTBuy Sheet Guide",
        item: `${siteConfig.siteUrl}/article/gtbuy-spreadsheet-guide`,
      },
    ],
  },
];

export default function ArticlePage() {
  return (
    <>
      <JsonLd data={articleJsonLd as unknown as Record<string, unknown>} />
      <Navbar />
      <main>
        <article>
          <section className="relative w-full h-[360px] md:h-[480px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20desk%20setup%20with%20open%20laptop%20displaying%20colorful%20spreadsheet%20charts%20next%20to%20neatly%20stacked%20fashion%20magazines%20and%20a%20coffee%20cup%20on%20warm%20cream%20desk%20surface%20soft%20morning%20light%20streaming%20through%20window%20editorial%20photography%20calm%20organized%20aesthetic&width=1600&height=900&seq=article-hero-img-01&orientation=landscape"
              alt="GTBuy Spreadsheet Ultimate Guide"
              title="The Ultimate GTBuy Sheet Guide 2025"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/35"></div>
            <div className="relative z-10 w-full text-center px-4 md:px-6 pt-16">
              <div className="max-w-3xl mx-auto">
                <span className="text-xs font-medium text-white/80 uppercase tracking-widest mb-3 inline-block font-[family-name:var(--font-label)]">
                  In-Depth Guide
                </span>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight font-[family-name:var(--font-heading)]">
                  {articleContent.title}
                </h1>
                <p className="text-sm md:text-base text-white/80 max-w-xl mx-auto mt-4 font-[family-name:var(--font-body)] font-light">
                  {articleContent.summary}
                </p>
              </div>
            </div>
          </section>

          <section className="w-full py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 font-[family-name:var(--font-body)]">
                <Link to="/" className="hover:text-foreground-800 transition-colors cursor-pointer">
                  Home
                </Link>
                <span>/</span>
                <span className="text-foreground-800">GTBuy Sheet Guide</span>
              </nav>

              <div className="flex flex-wrap gap-3 mb-10">
                {categories.slice(0, 8).map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-3 py-1.5 text-xs font-medium bg-background-100 text-foreground-600 rounded-full hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>

              {articleContent.sections.map((section, idx) => (
                <div key={idx} className="mb-10 md:mb-14">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4 font-[family-name:var(--font-heading)]">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4 font-[family-name:var(--font-body)] font-light"
                    >
                      {para}
                    </p>
                  ))}
                </div>
              ))}

              <div className="mt-12 pt-8 border-t border-background-200/60">
                <div className="bg-accent-100/50 border border-accent-200/60 rounded-lg p-6 md:p-8 text-center">
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2 font-[family-name:var(--font-heading)]">
                    Start Shopping Smarter Today
                  </h3>
                  <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto font-[family-name:var(--font-body)]">
                    Visit the main GTBuy platform to access the full spreadsheet comparison system across all product categories.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      href={siteConfig.mainSiteUrl}
                      target="_blank"
                      rel="noopener nofollow"
                      className="px-6 py-3 bg-primary-500 text-background-50 font-medium text-sm rounded-md hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      gtbuk sheet
                    </a>
                    <Link
                      to="/"
                      className="px-6 py-3 bg-background-50 text-foreground-800 font-medium text-sm rounded-md hover:bg-background-100 border border-background-200/60 transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      Back to Home
                    </Link>
                  </div>
                </div>
              </div>

              <div className="mt-10 bg-foreground-950 rounded-lg p-6 md:p-8">
                <h3 className="text-lg font-semibold text-background-50 mb-4 font-[family-name:var(--font-heading)]">
                  Explore Product Categories
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-background-300 hover:text-background-100 transition-colors py-1.5 whitespace-nowrap cursor-pointer"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>

              <div className="mt-8 text-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-950 transition-colors font-medium cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Return to GTBuy Spreadsheet Home
                </Link>
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}